/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack, StackItem } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useEntities } from 'src/luis/projects/entities/hooks/useEntities';
import { CompositeEntity } from 'src/luis/projects/entities/types/CompositeEntity';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { getCompositeEntityChildren } from 'src/luis/projects/entities/utils/entityHelpers';
import { ConversationFeaturePickerContainer } from 'src/luis/verticals/conversationFeaturePicker/ConversationFeaturePickerContainer';
import { CompositeEntityChildTable } from 'src/luis/verticals/entityDetails/components/compositeEntityDetails/CompositeEntityChildTable';
import { CompositeEntityChildTableCommandBar } from 'src/luis/verticals/entityDetails/components/compositeEntityDetails/CompositeEntityChildTableCommandBar';
import { compositeEntityDetailsMessages } from 'src/luis/verticals/entityDetails/components/compositeEntityDetails/compositeEntityDetailsMessages';

export type CompositeEntityDetailsProps = {
    /**
     * The composite entity to display details for.
     */
    entity: CompositeEntity;
};

/**
 * Represents the details for a composite entity which includes
 * listing and controlling the entity children.
 *
 * @param props The input properties for the entity details.
 */
export const CompositeEntityDetails = observer((props: CompositeEntityDetailsProps) => {
    const { entity } = props;
    const { selection, selectedItems } = useSelection<Entity>();
    const { localize } = useLocalization();
    const { entities } = useEntities();
    const { dispatcher } = useGlobalState();
    const [isDeleteChildrenModalHidden, setIsDeleteChildrenModalHidden] = React.useState(true);

    const deleteChildrenTracker = useProgressTracker({
        onComplete: () => {
            setIsDeleteChildrenModalHidden(true);
            selection.setAllSelected(false);
        }
    });

    // Get the entities that are not included in the composite children
    // and the roles name is parent:roleName for the add children drop down.
    const childrenToDisplay: Partial<Entity>[] = getCompositeEntityChildren(entities).filter(
        e => !entity.children.find(c => c.id === e.id)
    );

    const onAddChild = (child: Partial<Entity>): void =>
        dispatcher.dispatch('createChildEntityForCompositeEntity', {
            id: entity.id,
            childName: child.name,
            toastData: {
                running: { title: localize(compositeEntityDetailsMessages.createChildToastStart) },
                completed: { title: localize(compositeEntityDetailsMessages.createChildToastSuccess) },
                failed: { title: localize(compositeEntityDetailsMessages.createChildToastFailed) }
            }
        });

    const onDeleteChildren = (): void => {
        const isMultiple = selectedItems.length > 1;
        dispatcher.dispatch('deleteChildrenEntityForCompositeEntity', {
            id: entity.id,
            childIds: selectedItems.map(c => c.id),
            tracker: deleteChildrenTracker,
            toastData: {
                running: {
                    title: localize(
                        isMultiple
                            ? compositeEntityDetailsMessages.deleteChildToastStart
                            : compositeEntityDetailsMessages.deleteChildrenToastStart
                    )
                },
                completed: {
                    title: localize(
                        isMultiple
                            ? compositeEntityDetailsMessages.deleteChildToastSuccess
                            : compositeEntityDetailsMessages.deleteChildrenToastSuccess
                    )
                },
                failed: {
                    title: localize(
                        isMultiple
                            ? compositeEntityDetailsMessages.deleteChildToastFailed
                            : compositeEntityDetailsMessages.deleteChildrenToastFailed
                    )
                }
            }
        });
    };

    return (
        <Stack tokens={{ childrenGap: 10 }}>
            <ConversationFeaturePickerContainer telemetryContext="CompositeEntityDetails" model={entity} />

            <StackItem>
                <CompositeEntityChildTableCommandBar
                    childrenCount={entity.children.length}
                    childrenToDisplay={childrenToDisplay}
                    selectedItems={selectedItems}
                    onAddChild={onAddChild}
                    onDeleteChildren={() => setIsDeleteChildrenModalHidden(false)}
                />

                <CompositeEntityChildTable items={entity.children} selection={selection} />
            </StackItem>

            <DeletePromptModal
                hidden={isDeleteChildrenModalHidden}
                onDelete={onDeleteChildren}
                dialogContentProps={{
                    title: localize(compositeEntityDetailsMessages.deleteChildModalTitle),
                    subText: localize(compositeEntityDetailsMessages.deleteChildModalDescription)
                }}
                onDismiss={() => setIsDeleteChildrenModalHidden(true)}
            />
        </Stack>
    );
});
